import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import type { Cliente, Cotacao } from '@/db';
import { embedBrandLogo } from '@/utils/pdfLogo';
import { freteDoCliente, tipoFreteLabel } from './frete';

const BLUE = rgb(0.035, 0.18, 0.43);
const LIGHT_BLUE = rgb(0.93, 0.96, 0.99);
const TEXT = rgb(0.12, 0.16, 0.22);
const MUTED = rgb(0.38, 0.43, 0.5);
const BORDER = rgb(0.82, 0.86, 0.91);

function safeText(value: unknown) { return String(value ?? '').replace(/[–—]/g, '-'); }
function formatDate(value: Date) { return value.toLocaleDateString('pt-BR'); }
function wrap(text: string, font: PDFFont, size: number, maxWidth: number) {
  const words = safeText(text).split(/\s+/); const lines: string[] = []; let line = '';
  for (const word of words) { const next = line ? `${line} ${word}` : word; if (font.widthOfTextAtSize(next, size) <= maxWidth) line = next; else { if (line) lines.push(line); line = word; } }
  if (line) lines.push(line); return lines.length ? lines : [''];
}

export async function buildCotacaoPdf(cotacao: Cotacao, cliente: Cliente | null): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedBrandLogo(pdf);
  const pageSize: [number, number] = [595.28, 841.89];
  let page: PDFPage; let y: number;

  const addPage = () => {
    page = pdf.addPage(pageSize); y = 790;
    page.drawRectangle({ x: 0, y: 810, width: pageSize[0], height: 32, color: BLUE });
    page.drawText('ZSCHIMMER & SCHWARZ BRASIL', { x: 44, y: 822, size: 9, font: bold, color: rgb(1, 1, 1) });
    page.drawText('COTACAO', { x: 505, y: 822, size: 8, font: bold, color: rgb(1, 1, 1) });
    if (logo) page.drawImage(logo, { x: 44, y: 728, width: 72, height: 64 });
    page.drawText('Zschimmer & Schwarz do Brasil Ltda.', { x: 132, y: 768, size: 15, font: bold, color: BLUE });
    y = 704;
  };
  addPage();

  page!.drawRectangle({ x: 44, y: y - 74, width: 507, height: 74, color: LIGHT_BLUE, borderColor: BORDER, borderWidth: 0.7 });
  page!.drawText('CLIENTE', { x: 58, y: y - 20, size: 8, font: bold, color: BLUE });
  page!.drawText(safeText(cotacao.clienteNome), { x: 58, y: y - 39, size: 11, font: bold, color: TEXT });
  const local = cliente?.codigo === cotacao.clienteCodigo && cliente.cidade ? `   ${safeText(cliente.cidade)}/${safeText(cliente.uf)}` : '';
  page!.drawText(`Codigo: ${safeText(cotacao.clienteCodigo)}${local}`, { x: 58, y: y - 58, size: 9, font: regular, color: TEXT });
  y -= 96;

  const emissionDate = new Date(cotacao.createdAt);
  const date = formatDate(emissionDate);
  page!.drawText(`Emissao: ${date}`, { x: 44, y, size: 8.5, font: bold, color: TEXT });
  page!.drawText(`Moeda: ${cotacao.moeda}`, { x: 480, y, size: 8.5, font: bold, color: TEXT });
  y -= 25;

  const headers = ['Produto', 'Peso (kg)', 'Un.', 'Preco NET', 'Fator', 'ICMS', 'IPI', 'Preco final'];
  const xs = [44, 208, 254, 288, 346, 394, 428, 470];
  const widths = [164, 46, 34, 58, 48, 34, 42, 81];
  const drawHeader = () => {
    page!.drawRectangle({ x: 44, y: y - 22, width: 507, height: 22, color: BLUE });
    headers.forEach((header, index) => page!.drawText(header, { x: xs[index] + 4, y: y - 15, size: 7.5, font: bold, color: rgb(1, 1, 1) })); y -= 22;
  };
  drawHeader();

  for (const [index, item] of cotacao.itens.entries()) {
    const lines = wrap(`${item.materialCodigo} - ${item.materialDescricao}`, regular, 8, widths[0] - 8);
    const height = Math.max(26, lines.length * 10 + 8);
    if (y - height < 105) { addPage(); drawHeader(); }
    if (index % 2 === 0) page!.drawRectangle({ x: 44, y: y - height, width: 507, height, color: rgb(0.975, 0.982, 0.99) });
    lines.forEach((line, lineIndex) => page!.drawText(line, { x: 48, y: y - 15 - lineIndex * 10, size: 8, font: regular, color: TEXT }));
    const values = [item.peso ? item.peso.toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : '-', item.unidadeMedida, item.precoNegociado.toFixed(2), `${(item.fator * 100).toFixed(2)}%`, `${(item.icms * 100).toFixed(0)}%`, `${(item.ipi * 100).toFixed(2)}%`, (item.precoNegociadoComImpostos ?? item.precoComImpostosMinimo).toFixed(2)];
    values.forEach((value, valueIndex) => page!.drawText(value, { x: xs[valueIndex + 1] + 4, y: y - 16, size: 8, font: valueIndex === 6 ? bold : regular, color: TEXT }));
    page!.drawLine({ start: { x: 44, y: y - height }, end: { x: 551, y: y - height }, thickness: 0.5, color: BORDER }); y -= height;
  }

  y -= 18;
  if (y < 165) addPage();
  page!.drawText(`Condicoes de pagamento: ${safeText(cotacao.condicaoPagamento?.trim() || 'A combinar')}`, { x: 44, y: y - 18, size: 9, font: bold, color: TEXT });
  const frete = tipoFreteLabel(cotacao.frete || (cliente?.codigo === cotacao.clienteCodigo ? freteDoCliente(cliente) : ''), 'Nao informado');
  page!.drawText(`Frete: ${frete}`, { x: 44, y: y - 34, size: 9, font: bold, color: TEXT });
  page!.drawText('Cotacao valida por 7 dias.', { x: 44, y: y - 53, size: 8.5, font: bold, color: BLUE });
  page!.drawText('Precos com impostos, sem IPI.', { x: 44, y: y - 68, size: 8, font: regular, color: MUTED });
  page!.drawText(`Atenciosamente, ${safeText(cotacao.vendedor)}`, { x: 44, y: y - 85, size: 9, font: regular, color: TEXT });

  const pages = pdf.getPages();
  pages.forEach((current, index) => {
    current.drawLine({ start: { x: 44, y: 55 }, end: { x: 551, y: 55 }, thickness: 0.6, color: BORDER });
    current.drawText('Zschimmer & Schwarz do Brasil Ltda. | Documento confidencial', { x: 44, y: 38, size: 7, font: regular, color: MUTED });
    current.drawText(`Pagina ${index + 1} de ${pages.length}`, { x: 490, y: 38, size: 7, font: regular, color: MUTED });
  });
  return pdf.save();
}

export function cotacaoPdfFilename(cotacao: Cotacao) {
  return `cotacao-${cotacao.clienteCodigo}-${formatDate(new Date(cotacao.createdAt)).replaceAll('/', '-')}.pdf`;
}

export async function downloadCotacaoPdf(cotacao: Cotacao, cliente: Cliente | null, _showSensitive = true) {
  const bytes = await buildCotacaoPdf(cotacao, cliente);
  const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url;
  anchor.download = cotacaoPdfFilename(cotacao); anchor.click(); URL.revokeObjectURL(url);
}
